import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { Input } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { useBinaryState } from '../../../../../hooks/use-binary-state';
import { updateImportMapping } from '../../../data/api';
import { MappingTemplate } from '../../../data/types/mapping-template';
import { useImportContext } from '../import-context';
import { messages } from './messages';
import styles from './styles.module.css';

export const MappingTemplateEditableRow = ({ importTemplate, onStopEdit }) => {
  const { updateMappingTemplate, isMappingNameUnique } = useImportContext();
  const [templateName, setTemplateName] = useState(importTemplate.name);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUpdateMappingLoading, startLoading, stopLoading] = useBinaryState();
  const intl = useIntl();

  const validateName = (name) => {
    if (name?.trim().toLowerCase() !== importTemplate.name.trim().toLowerCase()) {
      const isValid = isMappingNameUnique(name);
      if (!isValid) {
        setErrorMessage(messages.templateNameRepeatedMessage);
      }
    }
  };

  const onSave = () => {
    if (!templateName.length) {
      setErrorMessage(messages.templateNameRequiredMessage);
    } else {
      startLoading();
      updateImportMapping({
        mappingId: importTemplate.mappingId,
        name: importTemplate.name,
        updatedName: templateName,
      })
        .then(() => {
          onStopEdit();
          updateMappingTemplate({
            mappingId: importTemplate.mappingId,
            updatedName: templateName,
          });
        })
        .catch(() => {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title: <FormattedMessage {...messages.renameTemplateErrorTitle} />,
            content: <FormattedMessage {...messages.renameTemplateErrorMessage} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
        })
        .finally(() => {
          stopLoading();
        });
    }
  };

  const onChange = (value) => {
    setErrorMessage(null);
    validateName(value);
    setTemplateName(value);
  };
  return (
    <tr>
      <td className={styles['name-cell']}>
        {isUpdateMappingLoading ? (
          <LoadingIndicator />
        ) : (
          <div className={styles['input-container']}>
            <Input
              className={styles['mapping-input']}
              onChange={onChange}
              placeholder={intl.formatMessage(messages.templateNameInputPlaceholder)}
              value={templateName}
            />
            {errorMessage && (
              <div className={styles.error}>
                <FormattedMessage {...errorMessage} />
              </div>
            )}
          </div>
        )}
      </td>
      <td className={styles['actions-cell']}>
        <Button className={styles['save-button']} disabled={isUpdateMappingLoading || errorMessage} onClick={onSave} theme='primary'>
          <FormattedMessage {...messages.templateRowSaveButton} />
        </Button>
        <Button className={styles['cancel-button']} disabled={isUpdateMappingLoading} onClick={onStopEdit} theme='link'>
          <FormattedMessage {...messages.templateRowCancelButton} />
        </Button>
      </td>
    </tr>
  );
};

MappingTemplateEditableRow.propTypes = {
  importTemplate: MappingTemplate.isRequired,
  onStopEdit: PropTypes.func.isRequired,
};
